import { cva } from 'class-variance-authority'
import { useTranslation } from 'react-i18next'

import { Button } from '@/components/ui/button'
import { Text } from '@/components/ui/Text'
import { cn } from '@/lib/utils'

import { agreedPlanRecap, planStepLabel } from './useThreadPlanExecution'

import {
  ThreadPlanCardCallbacks,
  ThreadPlanExecutionView,
  ThreadPlanStepState,
} from './threadPlan.types'

/**
 * Suggested plan card — Agent C.
 *
 * Renders inside the proposing assistant turn below prose (sibling of
 * `ThreadAnswerBody`). Never docked on the composer. Primary CTA is
 * **Sounds good** — the only approval. The page then begins the first
 * step (skill turn or workflow confirm).
 *
 * Per-step Start: the `current` step, and only that step, may also show a
 * Start control — gated on the optional `onStartStep` prop so a caller that
 * omits it gets today's card back unchanged. This exists so the assistant
 * can recommend the next step in prose and the reader can start it from the
 * card instead of typing "go".
 *
 * Prop contract shared with D via `threadPlan.types.ts`:
 * `onSoundsGood`, `onDismiss`, `onSkip`, `onRetry`, optional `onStartStep`,
 * plus `plan` (`ThreadPlanExecutionView`).
 *
 * v1 editing on the card: Skip + Dismiss only. Skip for the current step is
 * enabled while `lifecycle === 'active'` (D owns the step cursor). While
 * `proposed`, Skip stays disabled so D can own cursor-gated Skip without the
 * card inventing a cursor.
 *
 * Kind chips reuse the StepCapabilityChips cva recipe (violet skill / blue
 * workflow). No credit numbers.
 */

interface ThreadPlanCardProps extends ThreadPlanCardCallbacks {
  plan: ThreadPlanExecutionView
  className?: string
}

const kindChipVariants = cva(
  'inline-flex items-center rounded-full border px-2 py-0.5',
  {
    variants: {
      kind: {
        skill:
          'border-violet-200 bg-violet-50 text-violet-700 dark:border-violet-500/30 dark:bg-violet-500/15 dark:text-violet-300',
        workflow:
          'border-blue-200 bg-blue-50 text-blue-700 dark:border-blue-500/30 dark:bg-blue-500/15 dark:text-blue-300',
      },
    },
  },
)

function stepKindLabel(
  step: ThreadPlanStepState,
  t: (key: string) => string,
): string {
  if (step.kind === 'skill') {
    return t('threads.detail.plan.inThread')
  }
  return t('threads.detail.plan.workflow')
}

export function ThreadPlanCard(props: ThreadPlanCardProps) {
  const {
    plan,
    onSoundsGood,
    onDismiss,
    onSkip,
    onRetry,
    onStartStep,
    className,
  } = props

  const { t } = useTranslation('app')
  const { t: tCommon } = useTranslation('common')

  const isTerminal =
    plan.lifecycle === 'done' ||
    plan.lifecycle === 'dismissed' ||
    plan.lifecycle === 'superseded'

  if (isTerminal) {
    const record =
      plan.lifecycle === 'superseded'
        ? t('threads.detail.plan.replaced')
        : plan.lifecycle === 'dismissed'
          ? t('threads.detail.plan.dismissed')
          : t('threads.detail.plan.finished')

    return (
      <div
        className={cn('rounded-lg border border-border/60 px-3 py-2', className)}
        data-testid="thread-plan-card"
        data-lifecycle={plan.lifecycle}
      >
        <Text size="xs" color="muted" data-testid="thread-plan-terminal">
          {record}
        </Text>
      </div>
    )
  }

  const showSoundsGood = plan.lifecycle === 'proposed'
  const isActive = plan.lifecycle === 'active'
  // Skip is stubbed/disabled until active — D owns the step cursor.
  const canSkip = isActive

  return (
    <div
      className={cn(
        'max-w-prose space-y-3 rounded-lg border border-border bg-card px-3 py-3',
        className,
      )}
      data-testid="thread-plan-card"
      data-lifecycle={plan.lifecycle}
    >
      <div className="space-y-1">
        <Text size="sm" data-testid="thread-plan-title">
          {t('threads.detail.plan.suggested')}
        </Text>
        <Text size="xs" color="muted" data-testid="thread-plan-note">
          {isActive
            ? agreedPlanRecap(plan)
            : t('threads.detail.plan.note')}
        </Text>
      </div>

      <ol className="space-y-2" data-testid="thread-plan-steps">
        {plan.steps.map((step, index) => {
          const isFailed = isActive && step.status === 'failed'
          const isSkipped = step.status === 'skipped'
          const isCurrent = isActive && step.status === 'current'
          const showStartStep = isCurrent && Boolean(onStartStep)
          const spendsTwins = step.flags?.includes('spendsTwins') === true
          const postsSlack = step.flags?.includes('postsSlack') === true

          return (
            <li
              key={`${step.slug}-${index}`}
              className="flex flex-wrap items-center gap-2 text-sm"
              data-testid={`thread-plan-step-${index}`}
              data-slug={step.slug}
              data-kind={step.kind}
              data-status={step.status}
            >
              <span
                className="w-5 shrink-0 text-xs text-muted-foreground"
                data-testid={`thread-plan-step-${index}-ordinal`}
              >
                {index + 1}.
              </span>
              <span
                className={cn(
                  'min-w-0 flex-1 truncate font-medium',
                  isSkipped && 'text-muted-foreground line-through',
                )}
                data-testid={`thread-plan-step-${index}-name`}
              >
                {planStepLabel(step)}
              </span>
              <span
                className={kindChipVariants({ kind: step.kind })}
                data-testid={`thread-plan-step-${index}-kind`}
                data-capability-type={step.kind === 'skill' ? 'skill' : 'tool'}
              >
                <Text as="span" size="xs">
                  {stepKindLabel(step, t)}
                </Text>
              </span>
              {spendsTwins ? (
                <span
                  className="rounded-full border border-amber-200 bg-amber-50 px-2 py-0.5 text-xs text-amber-800 dark:border-amber-500/30 dark:bg-amber-500/15 dark:text-amber-300"
                  data-testid={`thread-plan-step-${index}-twins`}
                >
                  {t('threads.detail.plan.usesTwins')}
                </span>
              ) : null}
              {postsSlack ? (
                <span
                  className="rounded-full border border-border bg-muted/40 px-2 py-0.5 text-xs text-muted-foreground"
                  data-testid={`thread-plan-step-${index}-slack`}
                >
                  {t('workflows.stepLabel.postsToSlack')}
                </span>
              ) : null}
              {isSkipped ? (
                <span
                  className="text-xs text-muted-foreground"
                  data-testid={`thread-plan-step-${index}-skipped`}
                >
                  {t('threads.detail.plan.skipped')}
                </span>
              ) : null}
              {isFailed ? (
                <>
                  <span
                    className="text-xs text-muted-foreground"
                    data-testid={`thread-plan-step-${index}-failed`}
                  >
                    {t('threads.detail.plan.didntFinish')}
                  </span>
                  <Button
                    type="button"
                    size="sm"
                    variant="secondary"
                    data-testid={`thread-plan-step-${index}-retry`}
                    onClick={onRetry}
                  >
                    {tCommon('retry')}
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    variant="ghost"
                    data-testid={`thread-plan-step-${index}-skip`}
                    onClick={onSkip}
                  >
                    {tCommon('skip')}
                  </Button>
                </>
              ) : null}
              {showStartStep ? (
                <Button
                  type="button"
                  size="sm"
                  variant="secondary"
                  data-testid={`thread-plan-step-${index}-start`}
                  aria-label={t('threads.detail.plan.startStepAria', {
                    name: planStepLabel(step),
                  })}
                  onClick={() => onStartStep?.(index)}
                >
                  {t('threads.detail.plan.start')}
                </Button>
              ) : null}
            </li>
          )
        })}
      </ol>

      {showSoundsGood ? (
        <div className="flex flex-wrap gap-2">
          <Button
            type="button"
            variant="primary"
            size="sm"
            className="text-sm"
            data-testid="thread-plan-sounds-good"
            onClick={onSoundsGood}
          >
            {t('threads.detail.plan.soundsGood')}
          </Button>
          <Button
            type="button"
            size="sm"
            variant="ghost"
            data-testid="thread-plan-dismiss"
            onClick={onDismiss}
          >
            {tCommon('dismiss')}
          </Button>
        </div>
      ) : null}

      {isActive ? (
        <div className="flex flex-wrap gap-2">
          <Button
            type="button"
            size="sm"
            variant="ghost"
            data-testid="thread-plan-skip"
            disabled={!canSkip}
            onClick={onSkip}
          >
            {tCommon('skip')}
          </Button>
          <Button
            type="button"
            size="sm"
            variant="ghost"
            data-testid="thread-plan-dismiss"
            onClick={onDismiss}
          >
            {tCommon('dismiss')}
          </Button>
        </div>
      ) : null}
    </div>
  )
}
